import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Compass,
  FileText,
  PencilSimple,
  Target,
} from "@phosphor-icons/react";
import SeoPageShell from "@/components/SeoPageShell";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "crm_notes_after_sales_call";

const noteFields = [
  {
    icon: Compass,
    title: "What changed",
    copy: "Capture the shift in the commercial picture: a blocker moved, a stakeholder appeared, urgency changed, or the decision process became clearer.",
  },
  {
    icon: FileText,
    title: "What matters now",
    copy: "Keep the facts another person would need to understand the account without replaying the conversation or reading a transcript.",
  },
  {
    icon: PencilSimple,
    title: "What you committed to",
    copy: "Record promises, follow-ups, owners, and timing precisely enough that the next action is obvious when you reopen the account.",
  },
  {
    icon: Target,
    title: "What happens next",
    copy: "End with the next customer step and the condition that would move the relationship forward, not a vague 'follow up next week.'",
  },
];

const noteChecklist = [
  "The customer's current blocker or decision dependency",
  "The stakeholder who matters next and why",
  "Any commitment made by either side",
  "A material change in urgency, risk, intent, or scope",
  "The agreed next step and its timing",
  "Enough context to make the next conversation easier to prepare",
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function CrmNotesAfterSalesCallPage() {
  return (
    <SeoPageShell source={source}>
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-violet-700/10 blur-[135px]" />
          </div>

          <div className="relative mx-auto max-w-7xl">
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              How to Write CRM Notes After a Sales Call
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">without rewriting the whole conversation.</span>
            </h1>

            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              A useful CRM note is not a transcript summary. It preserves the few facts that changed the account: the blocker, the stakeholder, the commitment, the next step, and the relationship context you will need later.
            </p>

            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
                Sign up free
              </Link>
              <Link to="/demo" onClick={() => track("hero", "see_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">What belongs in the note</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Write for the next person who opens the account—even if that person is you.</h2>
              <p className="marketing-lead-copy mt-6">The note should reconstruct the state of the relationship quickly, not document every topic that appeared on the call.</p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {noteFields.map(({ icon: Icon, title, copy }, index) => (
                <article key={title} className="sales-call-review-card">
                  <div className="sales-call-review-card__topline">
                    <span className="sales-call-review-card__number">0{index + 1}</span>
                    <Icon size={21} weight="duotone" />
                  </div>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">From conversation to CRM</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Keep the decision-useful parts. Drop the meeting diary.</h2>
              <p className="marketing-lead-copy mt-6">The same call can produce pages of transcript and still need only a few paragraphs in the CRM.</p>
            </div>

            <div className="sales-call-review-example">
              <div className="sales-call-review-example__header">
                <span>Example</span>
                <span>Renewal conversation</span>
              </div>
              <div className="sales-call-review-example__quote">
                <span className="sales-call-review-speaker">CLIENT</span>
                <FileText size={22} weight="duotone" aria-hidden="true" />
                <blockquote>“The team still likes the product. The problem is that Priya left, nobody really owns adoption now, and Finance wants something concrete before they sign another year.”</blockquote>
              </div>
              <div className="sales-call-review-example__analysis">
                <div>
                  <span>WHAT CHANGED</span>
                  <p>The renewal risk is no longer product sentiment. Ownership disappeared after Priya left, and Finance now needs measurable value.</p>
                </div>
                <div>
                  <span>COMMITMENT</span>
                  <p>Send a concise usage/value snapshot before Friday and use the next conversation to validate what Finance needs to see.</p>
                </div>
              </div>
              <div className="sales-call-review-example__next">
                <span>CRM-READY NOTE</span>
                <p>Customer remains positive on the product, but renewal is at risk because adoption lost an internal owner after Priya left. Finance now needs measurable evidence of value. I will send a usage/value snapshot by Friday; next call should validate Finance's criteria and re-establish an internal owner for adoption.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <span className="sales-call-review-kicker">A useful CRM note checklist</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">If it will matter next time, make it easy to find.</h2>
              <p className="marketing-lead-copy mt-6">You do not need every sentence. You need the state of the account and the evidence behind the next move.</p>
            </div>
            <div className="grid gap-4">
              {noteChecklist.map((item) => (
                <div key={item} className="sales-call-review-check">
                  <CheckCircle size={21} weight="fill" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="conversation-intelligence-related mx-auto max-w-5xl">
            <div>
              <span className="sales-call-review-kicker">Go one step further</span>
              <h2>Turn the same conversation into a debrief, not just documentation.</h2>
              <p>Use a structured debrief when you also need to understand what changed, what you could have handled differently, and what to carry into the next call.</p>
            </div>
            <Link to="/sales-call-debrief-template" onClick={() => track("internal_link", "sales_call_debrief_template")} className="marketing-consistency-secondary-link product-feature-final__secondary">
              Sales call debrief template
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>

        <section className="px-5 pb-20 pt-4 sm:px-8 sm:pb-28">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">See CRM-ready output</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Let the call become a useful note before the context disappears.</h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">Callsect turns conversation evidence and relationship context into CRM-ready notes shaped to how you write—ready to copy or export into your workflow.</p>
            <div>
              <Link to="/product/crm-notes" onClick={() => track("final", "see_crm_notes")} className="product-feature-final__primary">See CRM Notes</Link>
              <Link to="/demo" onClick={() => track("final", "see_demo")} className="product-feature-final__secondary">See Demo<ArrowRight size={17} weight="bold" /></Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
